"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const COMBINING_MARKS_REGEX = /^[̀-ͯ]$/;
const COMBINING_MARKS_ALL_REGEX = /[̀-ͯ]/g;
const RE_MARK = /^\p{M}$/u;
const RE_LATIN = /^\p{Script=Latin}$/u;

const SPECIAL_MAP: Record<string, string> = {
  ø: "o",
  Ø: "O",
  đ: "d",
  Đ: "D",
  ł: "l",
  Ł: "L",
  æ: "ae",
  Æ: "AE",
  œ: "oe",
  Œ: "OE",
  ß: "ss",
  þ: "th",
  Þ: "Th",
  ð: "d",
};

const SPECIAL_CHARS_REGEX = /[øØđĐłŁæÆœŒßþÞð]/g;

function removeLatinAccent(char: string): string {
  return char
    .normalize("NFD")
    .replace(COMBINING_MARKS_ALL_REGEX, "")
    .replace(SPECIAL_CHARS_REGEX, (match) => SPECIAL_MAP[match] ?? match);
}

const EXAMPLES: { before: string; after: string }[] = [
  { before: "café", after: "cafe" },
  { before: "naïve", after: "naive" },
  { before: "Müller", after: "Muller" },
  { before: "señor", after: "senor" },
  { before: "Gauß", after: "Gauss" },
  { before: "Łódź", after: "Lodz" },
];

/**
 * Strips accents from Latin letters only.
 *
 * The text is composed to NFC first, so a decomposed "e" + U+0301 is handled like "é"
 * and every non-Latin script leaves exactly as it came in: Hangul and kana are not left
 * decomposed, and Cyrillic й and ё keep their breve and diaeresis. Only a Latin base
 * letter is split with NFD, stripped of its U+0300 to U+036F marks and run through the
 * special-letter table. A loose combining mark is dropped only straight after a Latin
 * letter.
 */
export function removeAccentsWithCount(text: string): {
  changed: number;
  output: string;
} {
  let output = "";
  let changed = 0;
  let afterLatin = false;
  for (const char of text.normalize("NFC")) {
    if (RE_MARK.test(char)) {
      if (afterLatin && COMBINING_MARKS_REGEX.test(char)) {
        changed += 1;
      } else {
        output += char;
      }
      continue;
    }
    afterLatin = RE_LATIN.test(char);
    const plain = afterLatin ? removeLatinAccent(char) : char;
    if (plain !== char) {
      changed += 1;
    }
    output += plain;
  }
  return { changed, output };
}

export function removeAccents(text: string): string {
  return removeAccentsWithCount(text).output;
}

/** English strings. A translated content file overrides these through `ui`. */
export const ACCENT_REMOVER_STRINGS = {
  textWithAccents: "Text with accents",
  elSenorMullerPidioUn: "El señor Müller pidió un café en Łódź…",
  plainText: "Plain text",
  charactersChanged:
    "one:{count} character changed|other:{count} characters changed",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  examples: "Examples",
} as const;

export default function AccentRemoverWidget() {
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);

  const { output, changed: changedCount } = removeAccentsWithCount(input);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  const locale = useToolLocale();
  const strings = useToolUi(ACCENT_REMOVER_STRINGS);
  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="accent-input"
        >
          {strings.textWithAccents}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="accent-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.elSenorMullerPidioUn}
          value={input}
        />
      </div>
      {/* Output */}
      {input.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="accent-output"
          >
            {strings.plainText}
          </label>
          <textarea
            className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="accent-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.charactersChanged, locale, changedCount, {
              count: fmt(changedCount),
            })}
          </p>
        </div>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Examples */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.examples}
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {EXAMPLES.map((example) => (
            <div
              className="rounded-lg border border-border bg-card p-3 text-center text-sm"
              key={example.before}
            >
              <span className="text-foreground">{example.before}</span>
              <span aria-hidden="true" className="mx-2 text-muted-foreground">
                →
              </span>
              <span className="text-muted-foreground">{example.after}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
